import { ReactNode } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

/** Palette colours that expose a `.main` we can tint the badge with. */
type ConfirmColor =
	| 'primary'
	| 'secondary'
	| 'error'
	| 'warning'
	| 'info'
	| 'success';

export interface ConfirmDialogProps {
	open: boolean;
	title: string;
	message?: ReactNode;
	children?: ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	confirmColor?: ConfirmColor;
	icon?: ReactNode;
	confirmDisabled?: boolean;
	busy?: boolean;
	busyLabel?: string;
	onConfirm: () => void;
	onCancel: () => void;
}

export default function ConfirmDialog({
	open,
	title,
	message,
	children,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	confirmColor = 'error',
	icon,
	confirmDisabled = false,
	busy = false,
	busyLabel,
	onConfirm,
	onCancel,
}: ConfirmDialogProps) {
	const theme = useTheme();

	const badgeIcon =
		icon ??
		(confirmColor === 'error' || confirmColor === 'warning' ? (
			<ErrorOutlineRoundedIcon />
		) : (
			<HelpOutlineRoundedIcon />
		));

	return (
		<Dialog
			open={open}
			onClose={busy ? undefined : onCancel}
			maxWidth="xs"
			fullWidth
			transitionDuration={{ enter: 160, exit: 110 }}
			slotProps={{
				paper: {
					elevation: 8,
					// The theme's MuiDialog override squares the corners below sm,
					// but paper-slot `sx` outranks styleOverrides in emotion's
					// ordering, so the breakpoint has to be restated here or a
					// full-bleed sheet keeps one rounded edge.
					sx: { borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden' },
				},
			}}
		>
			{/* 32px of padding per side leaves ~247px of content at 375px once the
			    48px badge and the dialog margin are counted. */}
			<DialogTitle
				sx={{ px: { xs: 2, sm: 4 }, pt: { xs: 3, sm: 4 }, pb: 2 }}
			>
				<Stack direction="row" spacing={2} alignItems="center">
					<Box
						sx={{
							width: 48,
							height: 48,
							flexShrink: 0,
							borderRadius: '50%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: `${confirmColor}.main`,
							bgcolor: (theme) =>
								alpha(theme.palette[confirmColor].main, 0.12),
							// theme-check-ignore -- icon sized to fill the 48px badge circle, no shared token
							'& svg': { fontSize: 26, display: 'block' },
						}}
					>
						{badgeIcon}
					</Box>
					<Typography
						variant="h6"
						fontWeight={theme.typography.fontWeightBold}
						sx={{
							lineHeight: theme.app.typography.lineHeightTight,
						}}
					>
						{title}
					</Typography>
				</Stack>
			</DialogTitle>

			<DialogContent sx={{ px: { xs: 2, sm: 4 }, pb: 2 }}>
				<Stack spacing={2} sx={{ pt: 0.5 }}>
					{children ?? (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{
								lineHeight:
									theme.app.typography.lineHeightRelaxed,
							}}
						>
							{message}
						</Typography>
					)}
				</Stack>
			</DialogContent>

			<DialogActions
				sx={{
					px: { xs: 2, sm: 4 },
					pb: 3.5,
					pt: 2,
					gap: 1.25,
					flexWrap: 'wrap',
				}}
			>
				<Button
					variant="outlined"
					onClick={onCancel}
					disabled={busy}
					sx={{
						fontWeight: theme.app.typography.fontWeightSemiBold,
						px: 2.75,
						py: 0.75,
						color: 'text.secondary',
						borderColor: 'divider',
						'&:hover': {
							borderColor: 'text.disabled',
							bgcolor: 'action.hover',
						},
					}}
				>
					{cancelLabel}
				</Button>
				<Button
					variant="contained"
					color={confirmColor}
					disableElevation
					onClick={onConfirm}
					disabled={busy || confirmDisabled}
					startIcon={
						busy ? (
							<CircularProgress size={16} color="inherit" />
						) : undefined
					}
					sx={{
						fontWeight: theme.typography.fontWeightBold,
						px: 3.25,
						py: 0.75,
					}}
				>
					{busy && busyLabel ? busyLabel : confirmLabel}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
